import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../../../models/Entities/CashFlow/Payment/Payment';

/**
 * Lógica del panel de flujo de caja, sin React ni store: períodos, montos por
 * moneda, antigüedad y series mensuales. Todo lo que se ve en pantalla sale de acá.
 */

// ── Período ───────────────────────────────────────────────────────────────

export type PeriodPreset = 'month' | 'quarter' | 'year' | 'custom';

export interface DateRange {
    from: Date;
    to: Date;
}

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

/** El rango de cada atajo, contado desde hoy. */
export const presetRange = (preset: PeriodPreset, today: Date = new Date()): DateRange => {
    const year = today.getFullYear();
    const month = today.getMonth();
    switch (preset) {
        case 'quarter': {
            const quarterStart = month - (month % 3);
            return { from: new Date(year, quarterStart, 1), to: endOfDay(new Date(year, quarterStart + 3, 0)) };
        }
        case 'year':
            return { from: new Date(year, 0, 1), to: endOfDay(new Date(year, 11, 31)) };
        case 'month':
        default:
            return { from: new Date(year, month, 1), to: endOfDay(new Date(year, month + 1, 0)) };
    }
};

export const toDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const inRange = (value: Date | string | null | undefined, range: DateRange): boolean => {
    const date = toDate(value);
    if (!date) return false;
    return date >= startOfDay(range.from) && date <= endOfDay(range.to);
};

export const monthKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const monthLabel = (key: string): string => {
    const [year, month] = key.split('-').map(Number);
    return `${MONTH_SHORT[month - 1]} ${String(year).slice(2)}`;
};

/** Las claves de mes del rango, de principio a fin, aunque no tengan datos. */
export const monthsOf = (range: DateRange): string[] => {
    const keys: string[] = [];
    const cursor = new Date(range.from.getFullYear(), range.from.getMonth(), 1);
    const limit = new Date(range.to.getFullYear(), range.to.getMonth(), 1);
    while (cursor <= limit && keys.length < 120) {
        keys.push(monthKey(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys;
};

// ── Deudas y pagos ────────────────────────────────────────────────────────

export const isPending = (debt: Debt): boolean => (debt.pendingAmount ?? 0) > 0 && !debt.isDebtSettled;

export const currencyOf = (item: { currencyCode?: string | null }): string => (item.currencyCode || 'ARS').toUpperCase();

/** Sentido de una deuda para el centro: cobra si es el acreedor, paga si es el deudor. */
export const directionOf = (debt: Debt, entityIdentifier: string): 'receivable' | 'payable' =>
    debt.creditor === entityIdentifier ? 'receivable' : 'payable';

/** La contraparte según el sentido: el deudor cuando cobramos, el acreedor cuando pagamos. */
export const counterpartOf = (debt: Debt, entityIdentifier: string): string =>
    directionOf(debt, entityIdentifier) === 'receivable' ? debt.debtor : debt.creditor;

/**
 * Historial de saldadas en los dos sentidos, sin repetir (una deuda donde el
 * centro figura de ambos lados llega por las dos listas), las más nuevas primero.
 */
export const settledDebts = (debtsAsCreditor: Debt[], debtsAsDebtor: Debt[]): Debt[] => {
    const seen = new Set<number>();
    return [...debtsAsCreditor, ...debtsAsDebtor]
        .filter((debt) => {
            if (isPending(debt) || seen.has(debt.id)) return false;
            seen.add(debt.id);
            return true;
        })
        .sort((a, b) => new Date(b.debtDate).getTime() - new Date(a.debtDate).getTime());
};

/** Monedas presentes, las de más movimiento primero. */
export const currenciesOf = (debts: Debt[], payments: Payment[]): string[] => {
    const weight = new Map<string, number>();
    [...debts, ...payments].forEach((item: any) => {
        const code = currencyOf(item);
        weight.set(code, (weight.get(code) ?? 0) + 1);
    });
    return Array.from(weight.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([code]) => code);
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Días entre la fecha de la deuda y hoy; 0 si no tiene fecha válida. */
export const daysSince = (value: Date | string | null | undefined, today: Date = new Date()): number => {
    const date = toDate(value);
    if (!date) return 0;
    return Math.max(0, Math.floor((startOfDay(today).getTime() - startOfDay(date).getTime()) / DAY_MS));
};

export type AgingBucket = 'b0_30' | 'b31_60' | 'b61_90' | 'b90plus';

export const AGING_BUCKETS: { key: AgingBucket; label: string; min: number; max: number }[] = [
    { key: 'b0_30', label: '0–30 días', min: 0, max: 30 },
    { key: 'b31_60', label: '31–60 días', min: 31, max: 60 },
    { key: 'b61_90', label: '61–90 días', min: 61, max: 90 },
    { key: 'b90plus', label: 'Más de 90 días', min: 91, max: Infinity }
];

export const agingBucket = (days: number): AgingBucket =>
    AGING_BUCKETS.find((bucket) => days >= bucket.min && days <= bucket.max)?.key ?? 'b90plus';

export interface AgingSlice {
    key: AgingBucket;
    label: string;
    amount: number;
    count: number;
}

/** Cuánto de lo pendiente cae en cada tramo de antigüedad, en una moneda. */
export const buildAging = (debts: Debt[], currency: string, today: Date = new Date()): AgingSlice[] => {
    const slices = AGING_BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, amount: 0, count: 0 }));
    debts.filter((debt) => isPending(debt) && currencyOf(debt) === currency).forEach((debt) => {
        const slice = slices.find((item) => item.key === agingBucket(daysSince(debt.debtDate, today)))!;
        slice.amount += debt.pendingAmount ?? 0;
        slice.count += 1;
    });
    return slices;
};

export interface Kpis {
    currency: string;
    receivable: number;
    receivableCount: number;
    /** Lo pendiente de cobro con más de 90 días. */
    receivableOverdue: number;
    payable: number;
    payableCount: number;
    collectedInPeriod: number;
    collectedCount: number;
    paidInPeriod: number;
    paidCount: number;
    /** Por cobrar menos por pagar. */
    net: number;
}

export interface KpiInput {
    debtsAsCreditor: Debt[];
    debtsAsDebtor: Debt[];
    paymentsReceived: Payment[];
    paymentsDone: Payment[];
    currency: string;
    range: DateRange;
    today?: Date;
}

export const buildKpis = (input: KpiInput): Kpis => {
    const today = input.today ?? new Date();
    const ofCurrency = <T extends { currencyCode?: string | null }>(items: T[]): T[] =>
        items.filter((item) => currencyOf(item) === input.currency);

    const receivable = ofCurrency(input.debtsAsCreditor).filter(isPending);
    const payable = ofCurrency(input.debtsAsDebtor).filter(isPending);
    const collected = ofCurrency(input.paymentsReceived).filter((payment) => inRange(payment.paymentDate, input.range));
    const paid = ofCurrency(input.paymentsDone).filter((payment) => inRange(payment.paymentDate, input.range));

    const sum = (items: { pendingAmount?: number }[]) => items.reduce((acc, item) => acc + (item.pendingAmount ?? 0), 0);
    const sumAmount = (items: { amount?: number }[]) => items.reduce((acc, item) => acc + (item.amount ?? 0), 0);

    const receivableTotal = sum(receivable);
    const payableTotal = sum(payable);

    return {
        currency: input.currency,
        receivable: receivableTotal,
        receivableCount: receivable.length,
        receivableOverdue: sum(receivable.filter((debt) => daysSince(debt.debtDate, today) > 90)),
        payable: payableTotal,
        payableCount: payable.length,
        collectedInPeriod: sumAmount(collected),
        collectedCount: collected.length,
        paidInPeriod: sumAmount(paid),
        paidCount: paid.length,
        net: receivableTotal - payableTotal
    };
};

// ── Series mensuales ──────────────────────────────────────────────────────

export interface MonthlyPoint {
    key: string;
    label: string;
    /** Deudas a cobrar emitidas en el mes (monto original). */
    issued: number;
    /** Cobros recibidos en el mes. */
    collected: number;
    /** Pagos hechos en el mes. */
    paid: number;
}

export const buildMonthlySeries = (input: {
    debtsAsCreditor: Debt[];
    paymentsReceived: Payment[];
    paymentsDone: Payment[];
    currency: string;
    range: DateRange;
}): MonthlyPoint[] => {
    const points = new Map<string, MonthlyPoint>(
        monthsOf(input.range).map((key) => [key, { key, label: monthLabel(key), issued: 0, collected: 0, paid: 0 }])
    );

    const add = (value: Date | string | null | undefined, field: 'issued' | 'collected' | 'paid', amount: number) => {
        const date = toDate(value);
        if (!date || !inRange(date, input.range)) return;
        const point = points.get(monthKey(date));
        if (point) point[field] += amount;
    };

    input.debtsAsCreditor
        .filter((debt) => currencyOf(debt) === input.currency)
        .forEach((debt) => add(debt.debtDate, 'issued', debt.originalAmount ?? 0));
    input.paymentsReceived
        .filter((payment) => currencyOf(payment) === input.currency)
        .forEach((payment) => add(payment.paymentDate, 'collected', payment.amount ?? 0));
    input.paymentsDone
        .filter((payment) => currencyOf(payment) === input.currency)
        .forEach((payment) => add(payment.paymentDate, 'paid', payment.amount ?? 0));

    return Array.from(points.values());
};

// ── Por contraparte ───────────────────────────────────────────────────────

export interface CounterpartSlice {
    identifier: string;
    name: string;
    amount: number;
    count: number;
    oldestDays: number;
}

/** Lo pendiente agrupado por contraparte, de mayor a menor. */
export const groupByCounterpart = (
    debts: Debt[],
    currency: string,
    counterpartOf: (debt: Debt) => string,
    nameOf: (identifier: string) => string,
    today: Date = new Date()
): CounterpartSlice[] => {
    const groups = new Map<string, CounterpartSlice>();
    debts.filter((debt) => isPending(debt) && currencyOf(debt) === currency).forEach((debt) => {
        const identifier = counterpartOf(debt);
        const group = groups.get(identifier) ?? { identifier, name: nameOf(identifier), amount: 0, count: 0, oldestDays: 0 };
        group.amount += debt.pendingAmount ?? 0;
        group.count += 1;
        group.oldestDays = Math.max(group.oldestDays, daysSince(debt.debtDate, today));
        groups.set(identifier, group);
    });
    return Array.from(groups.values()).sort((a, b) => b.amount - a.amount);
};

/** Iniciales para el avatar: "Glaxo Smith Kline" → "GS". */
export const initialsOf = (name: string): string => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
};

export const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;
